import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { playerDropCategories } from '@/lib/playerDropCategories';
import {
    crashMapCellSizes,
    crashMapDropCategories,
    crashMapRanges,
    type CrashMapCellSize,
    type CrashMapDropCategory,
    type CrashMapHeatmapSummary,
    type CrashMapRange,
} from '@shared/vantaCrashMapTypes';
import type { HeatmapStyle } from './HeatmapLayer';
import { rampCss } from './HeatmapLayer';

export type HeatmapFilters = {
    range: CrashMapRange;
    customFrom: string; //datetime-local
    customTo: string;
    categories: CrashMapDropCategory[];
    normalize: boolean;
    excludeMass: boolean;
    cell: CrashMapCellSize;
    style: HeatmapStyle;
};

const rangeLabels: Record<CrashMapRange, string> = {
    '24h': '24h',
    '7d': '7d',
    '30d': '30d',
    '90d': '90d',
    custom: 'Custom',
};

function Section({ title, children }: { title: string, children: React.ReactNode }) {
    return (
        <div className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</h3>
            {children}
        </div>
    );
}

function Segmented<T extends string | number>({ options, value, onChange, labels }: {
    options: readonly T[];
    value: T;
    onChange: (v: T) => void;
    labels?: (v: T) => string;
}) {
    return (
        <div className="flex rounded-md border overflow-hidden text-sm">
            {options.map((opt, i) => (
                <button
                    key={String(opt)}
                    type="button"
                    className={cn(
                        'flex-1 px-2 py-1.5',
                        i > 0 && 'border-l',
                        opt === value
                            ? 'bg-secondary text-secondary-foreground'
                            : 'text-muted-foreground hover:bg-muted',
                    )}
                    onClick={() => onChange(opt)}
                >
                    {labels ? labels(opt) : String(opt)}
                </button>
            ))}
        </div>
    );
}

function SummaryRow({ label, value, muted }: { label: string, value: React.ReactNode, muted?: boolean }) {
    return (
        <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{label}</span>
            <span className={cn('font-mono', muted && 'text-muted-foreground')}>{value}</span>
        </div>
    );
}

type Props = {
    filters: HeatmapFilters;
    setFilters: (upd: Partial<HeatmapFilters>) => void;
    summary?: CrashMapHeatmapSummary;
    isLoading: boolean;
    error?: string;
};


/**
 * Panneau de droite du mode « Crash heatmap ».
 */
export default function HeatmapPanel({ filters, setFilters, summary, isLoading, error }: Props) {
    const toggleCategory = (cat: CrashMapDropCategory) => {
        const has = filters.categories.includes(cat);
        setFilters({
            categories: has
                ? filters.categories.filter((c) => c !== cat)
                : crashMapDropCategories.filter((c) => c === cat || filters.categories.includes(c)),
        });
    };

    return (
        <div className="space-y-5 p-4">
            <Section title="Time range">
                <Segmented
                    options={crashMapRanges}
                    value={filters.range}
                    onChange={(range) => setFilters({ range })}
                    labels={(r) => rangeLabels[r]}
                />
                {filters.range === 'custom' && (
                    <div className="grid grid-cols-[auto_1fr] items-center gap-2 text-sm">
                        <label htmlFor="lm-from" className="text-muted-foreground">From</label>
                        <input
                            id="lm-from"
                            type="datetime-local"
                            className="h-8 rounded-md border bg-background px-2 text-sm"
                            value={filters.customFrom}
                            onChange={(e) => setFilters({ customFrom: e.target.value })}
                        />
                        <label htmlFor="lm-to" className="text-muted-foreground">To</label>
                        <input
                            id="lm-to"
                            type="datetime-local"
                            className="h-8 rounded-md border bg-background px-2 text-sm"
                            value={filters.customTo}
                            onChange={(e) => setFilters({ customTo: e.target.value })}
                        />
                    </div>
                )}
            </Section>

            <Section title="Drop categories">
                <div className="flex flex-wrap gap-1.5">
                    {crashMapDropCategories.map((cat) => {
                        const info = playerDropCategories[cat];
                        const active = filters.categories.includes(cat);
                        return (
                            <button
                                key={cat}
                                type="button"
                                title={info.description}
                                onClick={() => toggleCategory(cat)}
                                className={cn(
                                    'flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs',
                                    active ? 'text-foreground' : 'text-muted-foreground opacity-60 hover:opacity-100',
                                )}
                                style={active ? { borderColor: info.color } : undefined}
                            >
                                <span
                                    className="size-2 rounded-full"
                                    style={{ backgroundColor: active ? info.color : 'transparent', border: `1px solid ${info.color}` }}
                                />
                                {info.label}
                            </button>
                        );
                    })}
                </div>
            </Section>

            <Section title="Analysis">
                <div className="space-y-3">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <label htmlFor="lm-normalize" className="text-sm">Normalize by occupancy</label>
                            <p className="text-xs text-muted-foreground">
                                Divides drops by the player-hours spent in each cell, so busy areas
                                (city center, spawn) don't dominate just because more players are there.
                            </p>
                        </div>
                        <Switch
                            id="lm-normalize"
                            checked={filters.normalize}
                            onCheckedChange={(normalize) => setFilters({ normalize })}
                        />
                    </div>
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <label htmlFor="lm-mass" className="text-sm">Exclude mass incidents</label>
                            <p className="text-xs text-muted-foreground">
                                Ignores bursts of drops within the same minute (server hitch, game update),
                                which are not tied to a location.
                            </p>
                        </div>
                        <Switch
                            id="lm-mass"
                            checked={filters.excludeMass}
                            onCheckedChange={(excludeMass) => setFilters({ excludeMass })}
                        />
                    </div>
                </div>
            </Section>

            <Section title="Cell size">
                <Segmented
                    options={crashMapCellSizes}
                    value={filters.cell}
                    onChange={(cell) => setFilters({ cell })}
                    labels={(c) => `${c} m`}
                />
            </Section>

            <Section title="Style">
                <Segmented
                    options={['smooth', 'cells'] as const}
                    value={filters.style}
                    onChange={(style) => setFilters({ style })}
                    labels={(s) => (s === 'smooth' ? 'Smooth' : 'Cells')}
                />
                <div className="space-y-1">
                    <div className="h-2 rounded-sm" style={{ background: rampCss }} />
                    <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Low</span>
                        <span>{filters.normalize ? 'drops / player-hour' : 'drops'}</span>
                        <span>High</span>
                    </div>
                </div>
            </Section>

            <Section title="Summary">
                {error ? (
                    <p className="text-sm text-destructive">{error}</p>
                ) : (
                    <div className={cn('space-y-1', isLoading && 'opacity-60')}>
                        <SummaryRow label="Located drops" value={summary?.located ?? '—'} />
                        <SummaryRow label="Hot cells" value={summary?.hotCells ?? '—'} />
                        <SummaryRow label="Max per cell" value={summary?.maxPerCell ?? '—'} />
                        <SummaryRow label="Unlocated" value={summary?.unlocated ?? '—'} muted />
                        <SummaryRow label="Mass excluded" value={summary?.massExcluded ?? '—'} muted />
                    </div>
                )}
            </Section>
        </div>
    );
}
